import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useSubscription } from "@/context/SubscriptionContext";
import { hasAccess, MEMBERSHIP_URL } from "@/lib/subscriptionAccess";
import { BottomNavigation } from "@/components/layout/BottomNavigation";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  ExternalLink,
  Flame,
  Compass,
  BookOpen,
  Target,
  Lock,
  Sparkles,
  Crown
} from "lucide-react";

// Field Kit content for each quest
const FIELD_KITS = {
  "wake-up": {
    id: "wake-up",
    questId: "quest_wakeup",
    title: "Wake Up",
    stage: "Stage 1",
    tagline: "Before you can change direction, you need to know what's been running you.",
    briefing: "This stage is about seeing clearly — not fixing, not changing, just noticing. Most people skip this step entirely and wonder why nothing sticks. Your patterns have been running on autopilot for years. This kit gives you the tools to finally see them.",
    startWith: {
      resource: "Enneagram PDF",
      reason: "It will show you the pattern beneath the pattern."
    },
    questionToSitWith: "What do I keep doing that I said I would stop?",
    compassConnection: "Use your daily Mood and Energy trackers while working through this kit — you will start to see correlations you have never noticed before.",
    driveUrl: "https://drive.google.com/drive/folders/17ZqpwhiWIfrze1xbUkyVKyIu5Vksyjhr?usp=drive_link",
    icon: Flame,
    accentColor: "#D4A574"
  },
  "burn-the-map": {
    id: "burn-the-map",
    questId: "quest_burnmap",
    title: "Burn the Map",
    stage: "Stage 2",
    tagline: "This is where you do the uncomfortable work. These are your tools for it.",
    briefing: "Burning the map means releasing the version of yourself that was built by circumstance, expectation, and survival. This is the hardest stage and the most necessary one. You are not breaking down — you are breaking through.",
    startWith: {
      resource: "Shadow Work Journal",
      reason: "Start here before anything else. Do not skip it."
    },
    questionToSitWith: "What belief about myself am I most afraid to question?",
    compassConnection: "Use PathFinder during this stage — share what comes up in your journal entries and let it reflect patterns back to you.",
    driveUrl: "https://drive.google.com/drive/folders/1OJLcGy9bDn5EjkzNzh84W84DJ58Oyza5?usp=drive_link",
    icon: Target,
    accentColor: "#E07A5F"
  },
  "whole": {
    id: "whole",
    questId: "quest_whole",
    title: "Whole",
    stage: "Stage 3",
    tagline: "Regulation before transformation. You cannot build on a dysregulated foundation.",
    briefing: "Wholeness is not a destination — it is a daily practice of returning to yourself. This stage is about building the internal architecture that makes everything else sustainable: boundaries, nervous system regulation, and genuine self-acceptance.",
    startWith: {
      resource: "Nervous System Tracker",
      reason: "Track for 7 days before opening anything else in this kit."
    },
    questionToSitWith: "Where in my life am I performing wholeness instead of living it?",
    compassConnection: "Your Breath Reset and Breathing Exercise tools in Daily Compass are your anchors during this stage. Use them before journaling each day.",
    driveUrl: "https://drive.google.com/drive/folders/16LzYtedszwL87Qag8RF6QYN9RK0BWwKp?usp=drive_link",
    icon: Compass,
    accentColor: "#7B9E7B"
  },
  "purposeful-paths": {
    id: "purposeful-paths",
    questId: "quest_purposeful",
    title: "Purposeful Paths",
    stage: "Stage 4",
    tagline: "Direction without execution is just dreaming. This is your build phase.",
    briefing: "You have done the inner work. Now it is time to translate that into a life that actually reflects who you have become. This stage is about building systems, reclaiming your time, and moving with intention toward what genuinely matters to you.",
    startWith: {
      resource: "Leadership Mindset PDF",
      reason: "It will reframe how you think about your own authority in your life."
    },
    questionToSitWith: "What would I build if I stopped waiting to feel ready?",
    compassConnection: "Use your Focus Timer and Weekly Reflections tools consistently during this stage — structure and reflection working together is what makes this stage land.",
    driveUrl: "https://drive.google.com/drive/folders/1O0qkmvFDE7rlTKEQcpoWN15clSmY9wSI?usp=drive_link",
    icon: BookOpen,
    accentColor: "#5D9E9E"
  }
};

const UPGRADE_URL = "https://www.truenorth-zone.com/pages/compass-membership";

export default function FieldKitPage() {
  const { questSlug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { subscription } = useSubscription();
  
  const kit = FIELD_KITS[questSlug];
  
  // Determine subscription tier
  const isAnnual = hasAccess(subscription?.type, 'annual', user);
  const isPaid = hasAccess(subscription?.type, 'monthly', user); // Monthly or higher
  const isFree = !isPaid;
  
  if (!kit) {
    return (
      <div className="min-h-screen bg-[#0b2821] flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/60 mb-4">Quest not found</p>
          <Button onClick={() => navigate('/discover')} variant="outline">
            Back to Discover
          </Button>
        </div>
      </div>
    );
  }
  
  const KitIcon = kit.icon;
  
  return (
    <div className="min-h-screen bg-[#0b2821] pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#0b2821]/95 backdrop-blur-sm border-b border-[#a69373]/20">
        <div className="px-4 py-4 flex items-center gap-3">
          <button 
            onClick={() => navigate('/discover?tab=tools')}
            className="p-2 -ml-2 rounded-lg hover:bg-white/5 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-[#a69373]" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${kit.accentColor}20` }}>
              <KitIcon className="w-5 h-5" style={{ color: kit.accentColor }} />
            </div>
            <div>
              <p className="text-xs text-[#a69373]/60 font-medium uppercase tracking-wider">{kit.stage}</p>
              <h1 className="text-lg font-heading text-[#a69373]">{kit.title} Field Kit</h1>
            </div>
          </div>
        </div>
      </header>
      
      <main className="px-4 py-6 space-y-6 max-w-2xl mx-auto">
        {/* Tagline - Always visible */}
        <div className="text-center">
          <p className="text-white/80 text-lg italic leading-relaxed">
            "{kit.tagline}"
          </p>
        </div>
        
        {/* Free User View - Tagline + Full Upgrade Prompt */}
        {isFree && (
          <Card className="bg-[#0b2821] border border-[#a69373]/30 overflow-hidden">
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-[#a69373]/10 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-[#a69373]/60" />
              </div>
              <h3 className="text-white font-medium text-lg mb-2">Unlock Your Transformation Journey</h3>
              <p className="text-white/60 text-sm mb-6 max-w-sm mx-auto">
                The {kit.title} Field Kit contains powerful tools, exercises, and frameworks to guide your transformation. Upgrade to access this and all four stages.
              </p>
              <Button
                onClick={() => window.open(UPGRADE_URL, '_blank')}
                className="w-full sm:w-auto px-8"
                style={{ backgroundColor: '#a69373', color: '#ffffff' }}
              >
                <Crown className="w-4 h-4 mr-2" />
                Upgrade to Access Field Kits
              </Button>
            </CardContent>
          </Card>
        )}
        
        {/* Paid User View (Monthly/Quarterly/Annual) - Full Briefing */}
        {isPaid && (
          <>
            {/* Field Briefing Card */}
            <Card 
              className="overflow-hidden"
              style={{ 
                backgroundColor: 'rgba(45, 74, 68, 0.6)',
                borderLeft: `4px solid ${kit.accentColor}`,
                borderTop: '1px solid rgba(166, 147, 115, 0.2)',
                borderRight: '1px solid rgba(166, 147, 115, 0.2)',
                borderBottom: '1px solid rgba(166, 147, 115, 0.2)'
              }}
            >
              <CardContent className="p-6 space-y-6">
                {/* Section Header */}
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#a69373]" />
                  <h2 className="text-sm font-heading text-[#a69373] uppercase tracking-wider">Field Briefing</h2>
                </div>
                
                {/* Briefing Text */}
                <p className="text-white/90 leading-relaxed">
                  {kit.briefing}
                </p>
                
                {/* What to Start With */}
                <div className="space-y-2">
                  <h3 className="text-[#a69373] font-medium text-sm">What to start with</h3>
                  <p className="text-white/80 text-sm">
                    <span className="text-white font-medium">{kit.startWith.resource}</span>
                    <span className="text-white/60"> — {kit.startWith.reason}</span>
                  </p>
                </div>
                
                {/* Question to Sit With */}
                <div className="space-y-2">
                  <h3 className="text-[#a69373] font-medium text-sm">The question to sit with</h3>
                  <div 
                    className="p-4 rounded-lg"
                    style={{ backgroundColor: 'rgba(166, 147, 115, 0.1)' }}
                  >
                    <p className="text-white italic text-sm leading-relaxed">
                      "{kit.questionToSitWith}"
                    </p>
                  </div>
                </div>
                
                {/* Compass Connection */}
                <div className="space-y-2">
                  <h3 className="text-[#a69373] font-medium text-sm">How this connects to your daily practice in Compass</h3>
                  <p className="text-white/70 text-sm leading-relaxed">
                    {kit.compassConnection}
                  </p>
                </div>
              </CardContent>
            </Card>
            
            {/* Action Button */}
            <div className="pt-2">
              {isAnnual ? (
                /* Annual Users - Open Field Kit */
                <Button
                  onClick={() => window.open(kit.driveUrl, '_blank', 'noopener,noreferrer')}
                  className="w-full py-6 text-base font-medium transition-all hover:scale-[1.02]"
                  style={{ 
                    backgroundColor: '#a69373', 
                    color: '#ffffff',
                    boxShadow: '0 4px 20px rgba(166, 147, 115, 0.3)'
                  }}
                  data-testid="open-field-kit-btn"
                >
                  <ExternalLink className="w-5 h-5 mr-2" />
                  Open Your Field Kit
                </Button>
              ) : (
                /* Monthly/Quarterly Users - Upgrade Prompt */
                <Button
                  onClick={() => window.open(UPGRADE_URL, '_blank')}
                  className="w-full py-6 text-base font-medium transition-all hover:scale-[1.02]"
                  style={{ 
                    backgroundColor: 'transparent',
                    border: '2px solid #a69373',
                    color: '#a69373'
                  }}
                  data-testid="upgrade-annual-btn"
                >
                  <Crown className="w-5 h-5 mr-2" />
                  Upgrade to Annual to Access Your Field Kit
                </Button>
              )}
            </div>
            
            {/* Annual Badge for Annual Users */}
            {isAnnual && (
              <p className="text-center text-[#a69373]/60 text-xs">
                Your Field Kit opens in Google Drive for easy download and offline access
              </p>
            )}
            
            {/* Upgrade Note for Monthly/Quarterly */}
            {!isAnnual && (
              <p className="text-center text-white/40 text-xs">
                Annual members get full access to all four Field Kits and Transformation Quests
              </p>
            )}
          </>
        )}
      </main>
      
      <BottomNavigation />
    </div>
  );
}
